import { Component, Output, EventEmitter, NgZone, computed, effect, signal, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { BuzzerConfig } from '@app/common-codes/models/matwins-tenant/BuzzerConfig.model';
import { Translation, TranslationRelationType } from '@app/common-codes/models/matwins-tenant/navigation-entities/Translation.model';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { DateTimePickerComponent } from '@app/common-codes/components/nested-components/date-time-picker/date-time-picker.component';
import { SwitchButtonComponent } from '@app/common-codes/components/nested-components/switch-button/switch-button.component';

@Register(BuzzerConfig)
@Component({
  selector: 'app-buzzer-config-detail',
  templateUrl: './buzzer-config-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, MultiSelectComponent, FieldWrapperComponent, DateTimePickerComponent, SwitchButtonComponent, FieldContextDirective]
})
export class BuzzerConfigDetailComponent extends GenetricDetailComponent<BuzzerConfig> {
  override ComponentEntityClass = BuzzerConfig;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  protected override imageRelationName = 'FK_Image';
  @Input() fkPointOfServiceId: number | null = null;
  @Output() itemSaved = new EventEmitter<BuzzerConfig>();

  buzzerStatusLookup = signal<LookupItem[]>([]);
  translatedStatusLookup = signal<LookupItem[]>([]);
  employeeLookup = signal<LookupItem[]>([]);
  languageLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);

  /// Onglet de langue sélectionné (index dans languageLookup)
  selectedLanguageIndex = signal(0);

  /// Traductions saisies : Map<languageId, { subject: string, body: string }>
  translations = signal<Map<number, { subject: string; body: string }>>(new Map());

  /** Employes rattaches au POS courant (meme regle que l'ecran Visite). */
  readonly filteredEmployeeLookup = computed(() => {
    const employees = this.employeeLookup();
    const posId = this.editingEntity()?.fK_PointOfService;
    if (!employees || !posId) return employees;

    return employees.filter(employee =>
      `${employee.additionalData['pos_ids']}`.split(',').some(st => st.trim() === `${posId}`));
  });


  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        const rawStatus = this.lookupService.getLookupData('BuzzerStatus');
        this.buzzerStatusLookup.set(rawStatus);
        this.translatedStatusLookup.set(rawStatus.map(item => ({
          ...item,
          value: this.translationService.instant(`entities.buzzer-status.${(item.value || '').toLowerCase()}`)
        })));
        this.employeeLookup.set(this.getLookupData('Employee'));
        this.pointOfServiceLookup.set(this.getLookupData('PointOfService'));
        const langs = this.lookupService.getLookupData('i18nLanguage');
        this.languageLookup.set(langs);
        this.loadPos();
        this.loadTranslations(entity);

        // Restaure l'image apres le reset du parent (la base ne lit que la navigation imagePhoto).
        if (entity.image?.thumbnail_Path) {
          this.imageUrl = 'Documents/' + entity.image.thumbnail_Path;
        }
      }
    });
  }

  override getEmptyEntity(): BuzzerConfig {
    const config = new BuzzerConfig();
    config.status = 1; // Active
    config.show = false;
    config.startDate = new Date();
    if (this.fkPointOfServiceId) {
      config.fK_PointOfService = this.fkPointOfServiceId;
    }
    if (this.matwinsVariablesService.posId) {
      config.fK_PointOfService = this.matwinsVariablesService.posId;
    }
    config.fK_Employee = 0;
    config.subjectTranslations = [];
    config.bodyTranslations = [];
    return config;
  }

  /** Les FK retenues doivent apparaitre dans les multi-select (meme regle que l'ecran Visite). */
  private loadPos(): void {
    const entity = this.editingEntity();
    if (!entity) return;

    if (entity.fK_PointOfService && !entity.selectedPK_Pos?.length) {
      entity.selectedPK_Pos = [entity.fK_PointOfService.toString()];
    }
    if (entity.fK_Employee && !entity.selectedPK_Employee?.length) {
      entity.selectedPK_Employee = [entity.fK_Employee.toString()];
    }
  }

  private loadTranslations(entity: BuzzerConfig): void {
    const map = new Map<number, { subject: string; body: string }>();
    if (entity.subjectTranslations) {
      for (const t of entity.subjectTranslations) {
        const entry = map.get(t.fK_Language) || { subject: '', body: '' };
        entry.subject = t.translationValue;
        map.set(t.fK_Language, entry);
      }
    }
    if (entity.bodyTranslations) {
      for (const t of entity.bodyTranslations) {
        const entry = map.get(t.fK_Language) || { subject: '', body: '' };
        entry.body = t.translationValue;
        map.set(t.fK_Language, entry);
      }
    }
    this.translations.set(map);
  }

  getCurrentSubject(): string {
    const lang = this.languageLookup()[this.selectedLanguageIndex()];
    if (!lang) return '';
    return this.translations().get(Number(lang.key))?.subject ?? '';
  }

  getCurrentBody(): string {
    const lang = this.languageLookup()[this.selectedLanguageIndex()];
    if (!lang) return '';
    return this.translations().get(Number(lang.key))?.body ?? '';
  }

  onSubjectChange(value: string): void {
    const lang = this.languageLookup()[this.selectedLanguageIndex()];
    if (!lang) return;
    const map = new Map(this.translations());
    const entry = map.get(Number(lang.key)) || { subject: '', body: '' };
    entry.subject = value;
    map.set(Number(lang.key), entry);
    this.translations.set(map);
  }

  onBodyChange(value: string): void {
    const lang = this.languageLookup()[this.selectedLanguageIndex()];
    if (!lang) return;
    const map = new Map(this.translations());
    const entry = map.get(Number(lang.key)) || { subject: '', body: '' };
    entry.body = value;
    map.set(Number(lang.key), entry);
    this.translations.set(map);
  }

  selectLanguage(index: number): void {
    this.selectedLanguageIndex.set(index);
  }

  onEmployeeSelectChange(evt: string[]): void {
    this.editingEntity.update(entity => {
      if (!entity) return entity;
      const selectedIds = (evt ?? []).map(k => Number(k)).filter(n => Number.isFinite(n));
      entity.fK_Employee = selectedIds[0] ?? 0;
      entity.selectedPK_Employee = evt;
      return entity;
    });
  }

  onPOSselectChange(evt: string[]): void {
    this.editingEntity.update(entity => {
      if (!entity) return entity;
      const selectedIds = (evt ?? []).map(k => Number(k)).filter(n => Number.isFinite(n));
      entity.fK_PointOfService = selectedIds[0] ?? 0;
      entity.selectedPK_Pos = evt;
      return entity;
    });
  }

  onShowChange(value: boolean): void {
    this.editingEntity.update(entity => {
      if (entity) entity.show = value;
      return entity;
    });
  }

  onStatusChange(newStatus: number): void {
    this.editingEntity.update(entity => {
      if (entity) entity.status = newStatus;
      return entity;
    });
  }

  onDateTimePropretyChange(date: Date | null, propretyName: 'startDate' | 'endDate'): void {
    const entity = this.editingEntity();
    if (entity) {
      entity[propretyName] = date ?? undefined;
      const newEntity = Object.assign(Object.create(Object.getPrototypeOf(entity)), entity);
      this.editingEntity.set(newEntity);
    }
  }

  override parseToEntityInstance(): void {
    const target = this.EntityInstance;
    const entity = this.editingEntity();
    if (!target || !entity) return;

    // Les navigations sont retirees du payload : on garde celles de la liste pour l'affichage.
    const { image, employee, pointOfService } = target;

    Object.assign(target, entity);

    target.image = image;
    target.employee = employee;
    target.pointOfService = pointOfService;
  }

  private syncTranslationsToEntity(): void {
    const entity = this.editingEntity();
    if (!entity) return;
    const map = this.translations();
    const subjectList: Translation[] = [];
    const bodyList: Translation[] = [];

    map.forEach((value, langId) => {
      if (value.subject?.trim()) {
        const t = new Translation();
        t.fK_Language = langId;
        t.typeDeTraduction = 1; // Title
        t.translationValue = value.subject;
        t.compoosed_Id = Translation.buildId(TranslationRelationType.BuzzerTitle, 0);
        subjectList.push(t);
      }
      if (value.body?.trim()) {
        const t = new Translation();
        t.fK_Language = langId;
        t.typeDeTraduction = 2; // Description
        t.translationValue = value.body;
        t.compoosed_Id = Translation.buildId(TranslationRelationType.BuzzerBody, 0);
        bodyList.push(t);
      }
    });

    entity.subjectTranslations = subjectList;
    entity.bodyTranslations = bodyList;
  }

  private cleanNavigationBackReferences(): void {
    const entity = this.editingEntity();
    if (!entity) return;
    // Les FK scalaires portent la relation : aucune navigation ne doit partir dans le payload.
    entity.image = undefined;
    entity.employee = undefined;
    entity.pointOfService = undefined;
    if (entity.subjectTranslations) {
      entity.subjectTranslations.forEach(t => { t.language = undefined; });
    }
    if (entity.bodyTranslations) {
      entity.bodyTranslations.forEach(t => { t.language = undefined; });
    }
  }

  override validateForm(): boolean {
    // Validation standard (tous les champs enregistrés via bindingHandler)
    const baseValid = super.validateForm();
    let allValid = baseValid;

    // Validation manuelle du Subject et Body de la langue par défaut
    const defaultSubject = this.getCurrentSubject();
    const defaultBody = this.getCurrentBody();
    const errors: Record<string, string[]> = { ...this.validationErrors() };

    // R-15-077 / R-02-038 : l'employe destinataire est obligatoire.
    if (!this.editingEntity()?.fK_Employee) {
      errors['selectedPK_Employee'] = ["L'employé destinataire est obligatoire."];
      allValid = false;
    } else {
      delete errors['selectedPK_Employee'];
    }

    if (!defaultSubject || defaultSubject.trim().length < 2) {
      errors['subject'] = ['Le sujet est requis (min. 2 caractères).'];
      allValid = false;
    } else {
      delete errors['subject'];
    }

    if (!defaultBody || defaultBody.trim().length < 2) {
      errors['body'] = ['Le contenu est requis (min. 2 caractères).'];
      allValid = false;
    } else {
      delete errors['body'];
    }

    this.validationErrors.set(errors);
    return allValid;
  }

  async saveItem(): Promise<void> {
    this.syncTranslationsToEntity();
    this.cleanNavigationBackReferences();
    if (!this.validateForm()) return;
    const savedItem = await this.save();
    if (savedItem) { this.itemSaved.emit(savedItem); }
  }

  async saveAndCloseItem(): Promise<void> {
    this.syncTranslationsToEntity();
    this.cleanNavigationBackReferences();
    if (!this.validateForm()) return;
    const savedItem = await this.save();
    if (savedItem) { this.itemSaved.emit(savedItem); this.closeModal.emit(); }
  }
}
